import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Search,
  Eye,
  FileText,
  Calendar,
  X,
  Trash2,
  Download
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { API } from "@/lib/api";

export const InvoiceHistory = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [exporting, setExporting] = useState(false);

  const buildFilterParams = () => {
    const params = new URLSearchParams();
    if (search) params.append("search", search);
    if (dateRange.from) params.append("start_date", format(dateRange.from, "yyyy-MM-dd"));
    if (dateRange.to) params.append("end_date", format(dateRange.to, "yyyy-MM-dd"));
    return params;
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const params = buildFilterParams();
      const res = await fetch(`${API}/invoices/export.csv?${params}`);
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const disposition = res.headers.get("Content-Disposition") || "";
      const match = disposition.match(/filename="?([^";]+)"?/i);
      const filename = match ? match[1] : "customer_invoices.csv";
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Invoices exported");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export invoices");
    } finally {
      setExporting(false);
    }
  };

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const params = buildFilterParams();
      const response = await fetch(`${API}/invoices?${params}`);
      const data = await response.json();
      setInvoices(data);
    } catch (error) {
      console.error("Error fetching invoices:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const debounceTimer = setTimeout(fetchInvoices, 300);
    return () => clearTimeout(debounceTimer);
  }, [search, dateRange]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const clearFilters = () => {
    setSearch("");
    setDateRange({ from: null, to: null });
  };

  const handleDelete = async (id) => {
    try {
      const res = await fetch(`${API}/invoices/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error();
      setInvoices((prev) => prev.filter((inv) => inv.id !== id));
      toast.success("Invoice deleted");
    } catch {
      toast.error("Failed to delete invoice");
    } finally {
      setConfirmDeleteId(null);
    }
  };

  const hasFilters = search || dateRange.from || dateRange.to;

  return (
    <div className="space-y-6" data-testid="invoice-history-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Customer Invoice History</h1>
          <p className="text-slate-500 mt-1">All customer invoices</p>
        </div>
        <Button
          variant="outline"
          onClick={handleExportCSV}
          disabled={exporting || loading}
          className="gap-2"
          data-testid="export-csv-btn"
        >
          <Download className="w-4 h-4" />
          {exporting ? "Exporting..." : "Export CSV"}
        </Button>
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by invoice number, customer, or phone..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            
            {/* Date Range */}
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full lg:w-[240px] justify-start text-left font-normal",
                    !dateRange.from && "text-muted-foreground"
                  )}
                  data-testid="date-filter"
                >
                  <Calendar className="mr-2 h-4 w-4" />
                  {dateRange.from ? (
                    dateRange.to ? (
                      <>
                        {format(dateRange.from, "MMM dd")} - {format(dateRange.to, "MMM dd")}
                      </>
                    ) : (
                      format(dateRange.from, "MMM dd, yyyy")
                    )
                  ) : (
                    "Filter by date"
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <CalendarComponent
                  initialFocus
                  mode="range"
                  defaultMonth={dateRange.from}
                  selected={dateRange}
                  onSelect={setDateRange}
                  numberOfMonths={2}
                />
              </PopoverContent>
            </Popover>
            
            {/* Clear Filters */}
            {hasFilters && (
              <Button
                variant="ghost"
                onClick={clearFilters}
                className="text-slate-500"
                data-testid="clear-filters-btn"
              >
                <X className="w-4 h-4 mr-2" />
                Clear
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Invoice Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-pulse text-slate-500">Loading invoices...</div>
            </div>
          ) : invoices.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                    Invoice #
                  </TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                    Customer
                  </TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                    Date
                  </TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wide text-right">
                    Amount
                  </TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wide text-right">
                    Actions
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.map((invoice) => (
                  <TableRow 
                    key={invoice.id} 
                    className="table-row-hover"
                    data-testid={`invoice-row-${invoice.id}`}
                  >
                    <TableCell className="font-mono font-medium text-slate-900">
                      {invoice.invoice_number}
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium text-slate-900">{invoice.customer_name}</p>
                        <p className="text-sm text-slate-500">{invoice.phone}</p>
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-600">
                      {format(new Date(invoice.invoice_date), "MMM dd, yyyy")}
                    </TableCell>
                    <TableCell className="text-right font-mono font-medium text-slate-900">
                      {formatCurrency(invoice.total_payable)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Link to={`/invoice/${invoice.id}`}>
                          <Button variant="ghost" size="sm" className="btn-interactive" data-testid={`view-invoice-${invoice.id}`}>
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                        </Link>
                        {confirmDeleteId === invoice.id ? (
                          <div className="flex items-center gap-1">
                            <Button variant="destructive" size="sm" onClick={() => handleDelete(invoice.id)}>
                              Confirm
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => setConfirmDeleteId(null)}>
                              Cancel
                            </Button>
                          </div>
                        ) : (
                          <Button variant="ghost" size="sm" className="text-slate-400 hover:text-red-500" onClick={() => setConfirmDeleteId(invoice.id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="empty-state py-16">
              <FileText className="w-16 h-16 text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-700 mb-2">No invoices found</h3>
              <p className="text-slate-500 mb-6">
                {hasFilters
                  ? "Try adjusting your filters or search query"
                  : "No customer invoices yet"}
              </p>
              {!hasFilters && (
                <Link to="/vendor-invoice">
                  <Button className="bg-[#0047FF] hover:bg-[#0033CC]">
                    Create Invoice
                  </Button>
                </Link>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Results Summary */}
      {invoices.length > 0 && (
        <div className="text-sm text-slate-500 text-center">
          Showing {invoices.length} invoice{invoices.length !== 1 ? "s" : ""}
        </div>
      )}
    </div>
  );
};
